if (!customElements.get('yc-priority-product-recommendations')) {
  class YcPriorityProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.blocks = [];
      this.pools = [];
      this.poolIndexByKey = new Map();
      this.cardStyleSignatures = new Set();
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.addEventListener('click', this.handleClick);
      this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.removeEventListener('click', this.handleClick);
    }

    async load() {
      try {
        this.hidden = false;
        this.cardStyleSignatures.clear();
        this.collectConfiguration();

        if (!this.blocks.length) {
          this.hidden = this.dataset.designMode !== 'true';
          return;
        }

        const { assignments, reservedIds } = this.allocateManualProducts();
        const cards = this.collectManualProductCards();

        if (this.dataset.designMode === 'true') {
          this.renderDesignMode(assignments, cards);
          return;
        }

        let ruleRequestFailed = false;

        if (this.pools.length) {
          try {
            const storefrontApi = window.theme?.storefrontApi;
            if (!storefrontApi?.endpoint || !storefrontApi?.publicAccessToken || storefrontApi.publicAccessToken === 'YOUR_PUBLIC_STOREFRONT_TOKEN') {
              throw new Error('Configure window.theme.storefrontApi before using rule-based recommendations.');
            }

            const poolResults = await this.fetchPools(storefrontApi, reservedIds.size);
            const candidatesByBlock = this.collectRuleCandidates(poolResults, reservedIds);
            const ruleAssignments = await this.allocateRenderableRuleProducts(candidatesByBlock, reservedIds, cards);

            ruleAssignments.forEach((products, blockId) => assignments.set(blockId, products));
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            ruleRequestFailed = true;
            console.error('[YC Priority products]', error);
            this.blocks
              .filter((block) => block.type === 'rules')
              .forEach((block) => this.setBlockState(block.element, 'error', error.message));
          }
        }

        const assignedProducts = [...assignments.values()].flat();
        if (!assignedProducts.length && !ruleRequestFailed) {
          this.finishWithoutResults('No products were selected or matched.');
          return;
        }

        this.render(assignments, cards);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[YC Priority products]', error);
        this.showError(error.message);
      }
    }

    collectConfiguration() {
      this.blocks = [];
      this.pools = [];
      this.poolIndexByKey.clear();

      let currentCollectionHandles = [];
      try {
        currentCollectionHandles = JSON.parse(this.querySelector('[data-current-product-collections]')?.textContent || '[]').slice(0, 12);
      } catch (error) {
        console.error('[YC Priority products] Invalid current product collections.', error);
      }
      this.querySelectorAll('[data-recommendation-list]').forEach((element) => {
        const block = {
          element,
          id: element.dataset.blockId,
          type: element.dataset.displayType === 'manual' ? 'manual' : 'rules',
          limit: Number.parseInt(element.dataset.limit, 10) === 6 ? 6 : 4,
          hideWhenEmpty: element.dataset.hideWhenEmpty === 'true',
          products: [],
          poolGroups: []
        };

        if (block.type === 'manual') {
          try {
            block.products = JSON.parse(element.querySelector('[data-manual-products]')?.textContent || '[]');
          } catch (error) {
            console.error('[YC Priority products] Invalid selected products.', error);
          }
          this.blocks.push(block);
          return;
        }

        let priorities = [];
        try {
          priorities = JSON.parse(element.querySelector('[data-recommendation-priorities]')?.textContent || '[]');
        } catch (error) {
          console.error('[YC Priority products] Invalid recommendation fields.', error);
        }

        let selectedCollections = [];
        try {
          selectedCollections = JSON.parse(element.querySelector('[data-source-collections]')?.textContent || '[]');
        } catch (error) {
          console.error('[YC Priority products] Invalid source collections.', error);
        }
        const sortOrder = element.dataset.sortOrder === 'newest' ? 'newest' : 'best_selling';
        const collectionHandles = selectedCollections.length ? selectedCollections : currentCollectionHandles;

        priorities.slice(0, 6).forEach((priority) => {
          const values = this.normalizeValues(priority.values);
          const field = ['productType', 'productVendor'].includes(priority.field) ? priority.field : 'productMetafield';
          if (!values.length || (field === 'productMetafield' && (!priority.namespace || !priority.key))) return;

          const poolGroup = [];
          collectionHandles.forEach((collectionHandle) => {
            const poolKey = JSON.stringify({
              collectionHandle,
              field,
              namespace: priority.namespace,
              key: priority.key,
              values: [...values].sort(),
              sortOrder
            });
            let poolIndex = this.poolIndexByKey.get(poolKey);

            if (poolIndex === undefined) {
              poolIndex = this.pools.length;
              this.poolIndexByKey.set(poolKey, poolIndex);
              this.pools.push({ collectionHandle, field, namespace: priority.namespace, key: priority.key, values, sortOrder });
            }
            poolGroup.push(poolIndex);
          });

          if (poolGroup.length) block.poolGroups.push(poolGroup);
        });

        if (!block.poolGroups.length) {
          let message = 'Configure fields that have values on the current product.';
          if (!selectedCollections.length && !collectionHandles.length) {
            message = 'The current product does not belong to an available collection.';
          }
          this.setBlockState(element, 'empty', message);
        }
        this.blocks.push(block);
      });

      this.dataset.queryPoolCount = String(this.pools.length);

      if (window.Shopify?.designMode) {
        console.info('[YC Priority products] Query configuration', {
          poolCount: this.pools.length,
          blocks: this.blocks.map((block) => ({
            id: block.id,
            type: block.type,
            fieldPoolCount: block.poolGroups.flat().length
          }))
        });
      }
    }

    normalizeValues(rawValues) {
      const values = Array.isArray(rawValues) ? rawValues : [rawValues];
      return [...new Set(values.map((value) => {
        if (['string', 'number', 'boolean'].includes(typeof value)) return String(value).trim();
        if (value && typeof value === 'object' && value.handle) return String(value.handle).trim();
        return '';
      }).filter(Boolean))].slice(0, 249);
    }

    allocateManualProducts() {
      const currentProductId = this.dataset.currentProductId;
      const reservedIds = new Set([currentProductId]);
      const assignments = new Map(this.blocks.map((block) => [block.id, []]));

      this.blocks.filter((block) => block.type === 'manual').forEach((block) => {
        const configuredProducts = block.products.filter((product) => product.id && product.handle);
        const products = configuredProducts.slice(0, block.limit);

        configuredProducts.forEach((product) => reservedIds.add(product.id));
        assignments.set(block.id, products);
      });

      return { assignments, reservedIds };
    }

    collectManualProductCards() {
      const cards = new Map();
      this.querySelectorAll('template[data-manual-product-card]').forEach((template) => {
        if (!cards.has(template.dataset.productId)) cards.set(template.dataset.productId, template);
      });
      return cards;
    }

    async fetchPools(storefrontApi, reservedProductCount = 0) {
      const requestedProducts = this.getRequestedProductCount(reservedProductCount);
      const batches = [];
      const poolResults = Array.from({ length: this.pools.length }, () => []);
      let availableCollectionCount = 0;

      for (let index = 0; index < this.pools.length; index += 20) {
        batches.push(this.pools.slice(index, index + 20).map((pool, offset) => ({ pool, index: index + offset })));
      }

      const batchResults = await this.mapWithConcurrency(batches, 3, (batch) => (
        this.fetchPoolBatch(storefrontApi, batch, requestedProducts)
      ));

      batchResults.flat().forEach((result) => {
        poolResults[result.index] = result.products;
        if (result.collectionAvailable) availableCollectionCount += 1;
      });

      if (this.pools.length && availableCollectionCount === 0) {
        throw new Error('The source collections are not available to this Storefront API token and market.');
      }

      return poolResults;
    }

    async fetchPoolBatch(storefrontApi, batch, requestedProducts) {
      const variableDefinitions = ['$country: CountryCode!'];
      const selections = [];
      const variables = { country: (this.dataset.countryCode || 'US').toUpperCase() };

      batch.forEach(({ pool, index }) => {
        variableDefinitions.push(`$filters${index}: [ProductFilter!]!`, `$first${index}: Int!`);
        const newestFirst = pool.sortOrder === 'newest';
        const sortKey = newestFirst ? 'CREATED' : 'BEST_SELLING';
        const productsSelection = `products(first: $first${index}, sortKey: ${sortKey}, reverse: ${newestFirst}, filters: $filters${index}) { nodes { id handle } }`;

        variableDefinitions.push(`$handle${index}: String!`);
        selections.push(`pool${index}: collection(handle: $handle${index}) { ${productsSelection} }`);
        variables[`handle${index}`] = pool.collectionHandle;

        variables[`first${index}`] = requestedProducts;
        variables[`filters${index}`] = [{ available: true }, ...pool.values.map((value) => {
          if (pool.field === 'productType') return { productType: value };
          if (pool.field === 'productVendor') return { productVendor: value };
          return { productMetafield: { namespace: pool.namespace, key: pool.key, value } };
        })];
      });

      const query = `query YcPriorityProductRecommendations(${variableDefinitions.join(', ')}) @inContext(country: $country) { ${selections.join('\n')} }`;
      const response = await fetch(storefrontApi.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontApi.publicAccessToken },
        body: JSON.stringify({ query, variables }),
        signal: this.abortController.signal
      });
      if (!response.ok) throw new Error(`Storefront API request failed with ${response.status}.`);

      const payload = await response.json();
      if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join(' '));

      return batch.map(({ index }) => ({
        index,
        collectionAvailable: Boolean(payload.data?.[`pool${index}`]),
        products: payload.data?.[`pool${index}`]?.products?.nodes || []
      }));
    }

    getRequestedProductCount(reservedProductCount = 0) {
      const totalRuleSlots = this.blocks
        .filter((block) => block.type === 'rules')
        .reduce((sum, block) => sum + block.limit, 0);
      return Math.min((totalRuleSlots * 4) + Math.max(reservedProductCount, 0), 250);
    }

    collectRuleCandidates(poolResults, reservedIds) {
      const ruleBlocks = this.blocks.filter((block) => block.type === 'rules' && block.poolGroups.length);
      const candidatesByBlock = new Map();

      ruleBlocks.forEach((block) => {
        const candidates = [];
        const seenIds = new Set(reservedIds);
        for (let priorityIndex = 0; priorityIndex < block.poolGroups.length; priorityIndex += 1) {
          const poolGroup = block.poolGroups[priorityIndex];
          poolGroup.forEach((poolIndex) => {
            poolResults[poolIndex].forEach((product) => {
              if (!product.id || !product.handle || seenIds.has(product.id)) return;
              seenIds.add(product.id);
              candidates.push(product);
            });
          });
        }
        candidatesByBlock.set(block.id, candidates);
      });

      return candidatesByBlock;
    }

    allocateRuleProducts(candidatesByBlock, reservedIds) {
      const ruleBlocks = this.blocks.filter((block) => block.type === 'rules' && block.poolGroups.length);
      const usedIds = new Set(reservedIds);
      const assignments = new Map();

      ruleBlocks.forEach((block) => {
        const candidates = candidatesByBlock.get(block.id) || [];

        const products = [];
        for (const candidate of candidates) {
          if (usedIds.has(candidate.id)) continue;
          usedIds.add(candidate.id);
          products.push(candidate);
          if (products.length === block.limit) break;
        }
        assignments.set(block.id, products);
      });

      return assignments;
    }

    async allocateRenderableRuleProducts(candidatesByBlock, reservedIds, cards) {
      const unavailableIds = new Set();

      while (true) {
        const excludedIds = new Set([...reservedIds, ...unavailableIds]);
        const assignments = this.allocateRuleProducts(candidatesByBlock, excludedIds);
        const productsToFetch = [...assignments.values()].flat()
          .filter((product) => !cards.has(product.id));

        if (!productsToFetch.length) return assignments;

        const fetchedCards = await this.fetchProductCards(productsToFetch);
        fetchedCards.forEach((card, productId) => cards.set(productId, card));

        let foundUnavailableProduct = false;
        productsToFetch.forEach((product) => {
          if (fetchedCards.has(product.id)) return;
          unavailableIds.add(product.id);
          foundUnavailableProduct = true;
        });

        if (!foundUnavailableProduct) return assignments;
      }
    }

    async fetchProductCards(products) {
      const handles = [...new Set(products.map((product) => product.handle).filter(Boolean))];
      if (!handles.length) return new Map();
      const cards = new Map();
      const handleBatches = this.createHandleBatches(handles);

      const responses = await this.mapWithConcurrency(handleBatches, 3, async (handleBatch) => {
        const searchUrl = new URL(this.dataset.searchUrl, window.location.origin);
        searchUrl.searchParams.set('view', 'yc-priority-product-cards');
        searchUrl.searchParams.set('type', 'product');
        searchUrl.searchParams.set('q', handleBatch.map((handle) => `handle:${handle}`).join(' OR '));
        const response = await fetch(searchUrl, { headers: { Accept: 'text/html' }, signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Product card request failed with ${response.status}.`);
        return response.text();
      });

      responses.forEach((responseHtml) => {
        const container = document.createElement('div');
        container.innerHTML = responseHtml;
        container.querySelectorAll('template[data-product-id]').forEach((template) => {
          const card = template.content.firstElementChild;
          if (card) {
            cards.set(template.dataset.productId, card);
          }
        });
      });

      return cards;
    }

    createHandleBatches(handles) {
      const batches = [];
      let batch = [];
      let encodedQueryLength = 0;

      handles.forEach((handle) => {
        const queryPart = `${batch.length ? ' OR ' : ''}handle:${handle}`;
        const encodedPartLength = encodeURIComponent(queryPart).length;
        if (batch.length && (batch.length >= 40 || encodedQueryLength + encodedPartLength > 5500)) {
          batches.push(batch);
          batch = [];
          encodedQueryLength = 0;
        }
        const nextQueryPart = `${batch.length ? ' OR ' : ''}handle:${handle}`;
        batch.push(handle);
        encodedQueryLength += encodeURIComponent(nextQueryPart).length;
      });

      if (batch.length) batches.push(batch);
      return batches;
    }

    async mapWithConcurrency(items, concurrency, callback) {
      const results = new Array(items.length);
      let nextIndex = 0;
      const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
          const currentIndex = nextIndex;
          nextIndex += 1;
          results[currentIndex] = await callback(items[currentIndex], currentIndex);
        }
      });
      await Promise.all(workers);
      return results;
    }

    async handleClick(event) {
      const button = event.target.closest('.buy_now_btn');
      if (!button || !this.contains(button) || button.classList.contains('sold_out_btn') || button.classList.contains('loading')) return;

      event.preventDefault();
      const variantId = button.closest('.button_buy')?.dataset.id;
      if (!variantId) return;

      button.classList.add('loading');
      try {
        const rootUrl = window.theme?.routes?.root_url || '/';
        const normalizedRootUrl = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
        const response = await fetch(`${normalizedRootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!response.ok) throw new Error('Add to cart failed.');
        window.location.assign(`${normalizedRootUrl}cart`);
      } catch (error) {
        button.classList.remove('loading');
        console.error('[YC Priority products]', error);
      }
    }

    render(assignments, cards) {
      this.blocks.forEach((block) => {
        const cardElements = (assignments.get(block.id) || [])
          .map((product) => this.createCardElement(cards.get(product.id)))
          .filter(Boolean);
        block.element.querySelector('[data-recommendation-products]').replaceChildren(...cardElements);

        if (!cardElements.length && ['empty', 'error'].includes(block.element.dataset.state)) {
          this.applyBlockVisibility(block);
          return;
        }

        this.setBlockState(block.element, cardElements.length ? 'loaded' : 'empty', cardElements.length ? '' : 'No products were selected or matched.');
        this.applyBlockVisibility(block);
      });
      this.updateSectionVisibility();
    }

    renderDesignMode(assignments, cards) {
      this.blocks.forEach((block) => {
        if (block.type === 'rules') {
          this.setBlockState(block.element, 'loaded', '');
          return;
        }

        const cardElements = (assignments.get(block.id) || [])
          .map((product) => this.createCardElement(cards.get(product.id)))
          .filter(Boolean);

        block.element.querySelector('[data-recommendation-products]').replaceChildren(...cardElements);
        this.setBlockState(
          block.element,
          cardElements.length ? 'loaded' : 'empty',
          cardElements.length ? '' : 'Select products to preview this list.'
        );
      });
    }

    createCardElement(card) {
      const element = card instanceof HTMLTemplateElement
        ? card.content.firstElementChild?.cloneNode(true)
        : card;
      element?.querySelectorAll('script').forEach((script) => script.remove());
      element?.querySelectorAll('style').forEach((style) => {
        const signature = style.textContent.trim();
        if (this.cardStyleSignatures.has(signature)) style.remove();
        else this.cardStyleSignatures.add(signature);
      });
      return element;
    }

    setBlockState(element, state, message) {
      element.dataset.state = state;
      const status = element.querySelector('[data-recommendation-status-text]');
      if (status) status.textContent = message;
      element.hidden = false;
    }

    applyBlockVisibility(block) {
      block.element.hidden = this.dataset.designMode !== 'true'
        && block.hideWhenEmpty
        && block.element.dataset.state === 'empty';
    }

    updateSectionVisibility() {
      this.hidden = this.dataset.designMode !== 'true'
        && this.blocks.length > 0
        && this.blocks.every((block) => block.element.hidden);
    }

    finishWithoutResults(message) {
      this.querySelectorAll('[data-recommendation-list]').forEach((element) => {
        if (element.dataset.state !== 'empty') this.setBlockState(element, 'empty', message);
        const block = this.blocks.find((item) => item.element === element);
        if (block) this.applyBlockVisibility(block);
      });
      this.updateSectionVisibility();
    }

    showError(message) {
      this.querySelectorAll('[data-recommendation-list]').forEach((element) => this.setBlockState(element, 'error', message));
    }
  }

  customElements.define('yc-priority-product-recommendations', YcPriorityProductRecommendations);
}
